import React from 'react';
import { Box, Button, Theme, Typography, Divider } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { TabletButton } from '@campfire/tablet-button';
import { createStyles, makeStyles } from '@material-ui/styles';
import {
  Delete,
  Edit,
  ExpandMore,
  FileCopy,
  KeyboardArrowDown,
  KeyboardArrowUp,
  SwapHoriz,
  Close,
  BorderColorOutlined,
  Group,
  Visibility,
} from '@material-ui/icons';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { ADMIN_CONSOLE_GET_SINGLE_VOLUNTEER_PROFILE_TASK } from '../../admin-console/admin-console-content-pages/admin-console-volunteer-profile/admin-console-volunteer-profile.gql';
import {
  AdminConsoleGetSingleVolunteerProfileTask,
  AdminConsoleGetSingleVolunteerProfileTaskVariables,
} from '../../admin-console/admin-console-content-pages/admin-console-volunteer-profile/__generated__/AdminConsoleGetSingleVolunteerProfileTask';
import { useCampfireLazyQuery } from '../../../../global/network/useCampfireLazyQuery';
import { TaskItemField } from './TaskItemField';
import { deserializeTaskItems } from '../utils';
import { MoveToDialog } from './MoveToDialog';
import { TooltipMenu } from '../../../../common/tooltip-menu';
import { RoleIcon } from '../../../../common/icons/RoleIconList';
import { TaskVisibilityPreviewModal } from './TaskVisibilityPreviewModal';

const TaskDetailSkeleton = () => {
  return (
    <Box>
      <Box width='100%' mb='16px'>
        <Skeleton variant='text' width='180px' height='44px' style={{ borderRadius: '4px', marginBottom: '4px' }} />
        <Divider />
      </Box>
      <Box width='100%'>
        <Skeleton variant='rect' width='100%' height='20px' style={{ borderRadius: '4px', marginBottom: '12px' }} />
        <Skeleton variant='rect' width='100%' height='20px' style={{ borderRadius: '4px', marginBottom: '12px' }} />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '40px',
    },
    field: {
      '& .header': {
        display: 'flex',
        borderBottom: `1px solid ${theme.color.grey.neutralBrand200}`,
        paddingBottom: '12px',
        alignItems: 'center',
      },
      '& .title': {
        fontWeight: 700,
        fontSize: '18px',
        color: theme.color.grey.neutralBrand800,
        flex: 1,
      },
      '& .subTitle': {
        fontWeight: 300,
        fontSize: '16px',
        color: theme.color.grey.neutralBrand500,
        marginTop: '20px',
      },
      '& .subTitle2': {
        fontWeight: 300,
        fontSize: '14px',
        color: theme.color.grey.neutralBrand500,
        marginTop: '20px',
      },
      '& .button': {
        textTransform: 'none',
        fontSize: '14px',
        fontWeight: 600,
        padding: '8px',
        minWidth: 'unset',
        minHeight: 'unset',
        height: '30px',
      },
      '& .buttonGroup': {
        display: 'flex',
        gap: '12px',
      },
    },
    visibilityField: {
      marginTop: 36,
      '& .title': {
        fontWeight: 700,
        fontSize: 14,
        color: theme.color.grey.neutralBrand800,
      },
      '& .item': {
        fontSize: '9px',
        fontWeight: 500,
        padding: 10,
        borderRadius: 3,
        backgroundColor: '#D9D9D9',
        color: theme.color.grey.neutral400,
      },
      '& .items': {
        marginTop: '5px',
        display: 'flex',
        gap: 10,
      },
    },
    showMoreButton: {
      textTransform: 'none',
      borderRadius: '8px',
    },
    optionItem: {
      color: theme.alert.blue.extraLight,
      display: 'flex',
      alignItems: 'center',
      paddingTop: '10px',
      justifyContent: 'center',
      '& p': {
        fontSize: '14px',
      },
      '& svg': {
        fontSize: '16px',
      },
      textTransform: 'none',
    },
  })
);

interface TaskDetailsProps {
  taskId?: string | null;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onMove: (newTask: any, handleSuccess: () => void) => void;
  onClose?: () => void;
  cakes: Array<{ cakeId: string; title: string }>;
}

export function TaskDetails({ taskId, onEdit, onDuplicate, onDelete, cakes, onMove, onClose }: TaskDetailsProps) {
  const [taskQuery, { data, loading }] = useCampfireLazyQuery<
    AdminConsoleGetSingleVolunteerProfileTask,
    AdminConsoleGetSingleVolunteerProfileTaskVariables
  >(ADMIN_CONSOLE_GET_SINGLE_VOLUNTEER_PROFILE_TASK);
  const [showAll, setShowAll] = React.useState<boolean>(false);
  const [openMoveToDialog, setOpenMoveToDialog] = React.useState<boolean>(false);
  const [openPreviewDialog, setOpenPreviewDialog] = React.useState<boolean>(false);
  const { theme } = useCampfireTheme();

  React.useEffect(() => {
    if (taskId) {
      taskQuery({ variables: { taskId } });
    }
  }, [taskId]);

  const { task } = data?.vm || {};
  const isFixedTask = task?.order === -1;
  const cakeOptions = cakes.filter((cake) => cake.cakeId !== task?.cake.cakeId);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const onOption = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onOptionClose = () => setAnchorEl(null);

  const classes = useStyles();

  const parseTaskItems = deserializeTaskItems(task?.taskItems);
  const programText = React.useMemo(() => {
    const programNumber = task?.programs?.length;
    switch (programNumber) {
      case 0:
        return 'all programs';
      case 1:
        return task?.programs[0].name;
      default:
        return `${programNumber} programs`;
    }
  }, [task]);

  const activityText = React.useMemo(() => {
    if (task?.requiresAnyActivity) return 'any activity, including ones added later';
    const activityNumber = task?.activities?.length;
    switch (activityNumber) {
      case 0:
        return 'all activities';
      case 1:
        return task?.activities[0].name;
      default:
        return `${activityNumber} activities`;
    }
  }, [task]);

  const roleText = React.useMemo(() => {
    const roleNumber = task?.roles.length;
    switch (roleNumber) {
      case 0:
        return 'all roles';
      case 1:
        return `the ${task?.roles[0].name} role`;
      default:
        return `${roleNumber} roles`;
    }
  }, [task]);

  const rootTaskIds = parseTaskItems.allIds
    .filter((taskItemId) => !parseTaskItems.byTriggerOption[taskItemId])
    .filter((taskItemId) => parseTaskItems.byId[taskItemId].__typename === 'VOLUNTEER_TaskItemFieldType');

  return (
    <Box className={classes.container}>
      {loading ? (
        <TaskDetailSkeleton />
      ) : (
        <Box className={classes.field}>
          {task && (
            <Box display='flex' alignItems='center' style={{ marginBottom: '0px', color: '#9e9e9e' }}>
              {task.refereeChecksRequired && task.refereeChecksRequired > 0 ? (
                <Group style={{ fontSize: '14px', marginRight: '6px' }} />
              ) : task.isEventTask ? (
                <RoleIcon iconName='calendar-clock' size={0.7} style={{ marginRight: '6px' }} />
              ) : task.stripeConfig ? (
                <RoleIcon iconName='credit-card' size={0.7} style={{ marginRight: '6px' }} />
              ) : task.managerCompleted ? (
                <RoleIcon iconName='account-check' size={0.7} style={{ marginRight: '6px' }} />
              ) : (
                <BorderColorOutlined style={{ fontSize: '14px', marginRight: '6px' }} />
              )}
              <Typography style={{ fontSize: '12px', fontWeight: 500, color: '#9e9e9e' }}>
                {task.refereeChecksRequired && task.refereeChecksRequired > 0
                  ? 'Referee Check'
                  : task.isEventTask
                  ? 'Event Task'
                  : task.stripeConfig
                  ? 'Payment Task'
                  : task.managerCompleted
                  ? 'Manager Task'
                  : 'User Task'}
              </Typography>
            </Box>
          )}
          <Box className='header'>
            <Typography className='title'>{task?.title}</Typography>
            <Box className='buttonGroup'>
              <TabletButton
                size='small'
                variant='outlined'
                color='primary'
                aria-controls='task-status-menu'
                aria-haspopup='true'
                onClick={() => onEdit()}
                startIcon={<Edit />}
                style={{
                  border: '1px solid #9e9e9e',
                  boxSizing: 'border-box',
                  borderRadius: '4px',
                  padding: '2px 7px',
                  color: theme.color.grey.neutral300,
                  marginTop: '5px',
                }}
              >
                {'Edit'}
              </TabletButton>
              {isFixedTask ? null : (
                <Box display='flex'>
                  <TooltipMenu
                    open={Boolean(anchorEl)}
                    onClose={onOptionClose}
                    title={
                      <Box display='flex' flexDirection='column' alignItems='flex-start' style={{ padding: '0 5px' }}>
                        <Button
                          className={classes.optionItem}
                          startIcon={<Visibility />}
                          onClick={() => {
                            setOpenPreviewDialog(true);
                            onOptionClose();
                          }}
                        >
                          Preview
                        </Button>

                        <Button className={classes.optionItem} startIcon={<FileCopy />} onClick={onDuplicate}>
                          Duplicate
                        </Button>

                        {cakeOptions.length && (
                          <Button
                            onClick={() => {
                              setOpenMoveToDialog(true);
                              onOptionClose();
                            }}
                            className={classes.optionItem}
                            startIcon={<SwapHoriz />}
                          >
                            Move Category
                          </Button>
                        )}

                        <Button
                          className={classes.optionItem}
                          style={{ color: '#E5B3B3' }}
                          startIcon={<Delete />}
                          onClick={onDelete}
                        >
                          Delete
                        </Button>
                      </Box>
                    }
                  >
                    <TabletButton
                      size='small'
                      variant='outlined'
                      color='primary'
                      aria-controls='task-status-menu'
                      aria-haspopup='true'
                      onClick={(e) => onOption(e)}
                      endIcon={<ExpandMore />}
                      style={{
                        border: '1px solid #9e9e9e',
                        boxSizing: 'border-box',
                        borderRadius: '4px',
                        padding: '2px 7px',
                        color: theme.color.grey.neutral300,
                        marginTop: '5px',
                      }}
                    >
                      {'Options'}
                    </TabletButton>
                  </TooltipMenu>
                  {onClose && (
                    <TabletButton
                      size='small'
                      variant='outlined'
                      onClick={() => onClose()}
                      style={{
                        border: '1px solid #ff5252',
                        boxSizing: 'border-box',
                        borderRadius: '4px',
                        padding: '4px',
                        minWidth: '32px',
                        color: '#ff5252',
                        marginTop: '5px',
                        marginLeft: '8px',
                      }}
                    >
                      <Close style={{ fontSize: '18px' }} />
                    </TabletButton>
                  )}
                </Box>
              )}
            </Box>
          </Box>
          <Typography className={'subTitle'}>{task?.description}</Typography>
        </Box>
      )}

      {/* Referee Check Configuration Section */}
      {task?.refereeChecksRequired && task.refereeChecksRequired > 0 && !loading && (
        <Box className={classes.field}>
          <Box className='header' style={{ marginBottom: '20px' }}>
            <Typography className='title'>Referee Check Configuration</Typography>
          </Box>
          <Box display='flex' flexDirection='column' style={{ gap: '16px' }}>
            <Box>
              <Typography variant='body2' style={{ fontWeight: 600, marginBottom: '4px' }}>
                Required References
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                {task.refereeChecksRequired} reference{task.refereeChecksRequired !== 1 ? 's' : ''} required
              </Typography>
            </Box>
            {task.description && (
              <Box>
                <Typography variant='body2' style={{ fontWeight: 600, marginBottom: '4px' }}>
                  Instructions for Volunteers
                </Typography>
                <Typography variant='body2' color='textSecondary' style={{ whiteSpace: 'pre-wrap' }}>
                  {task.description}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Stripe Payment Configuration Section */}
      {task?.stripeConfig && !loading && (
        <Box className={classes.field}>
          <Box className='header' style={{ marginBottom: '20px' }}>
            <Typography className='title'>Payment Configuration</Typography>
          </Box>
          <Box display='flex' flexDirection='column' style={{ gap: '16px' }}>
            <Box>
              <Typography variant='body2' style={{ fontWeight: 600, marginBottom: '4px' }}>
                Product Type
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                {task.stripeConfig.stripeProductType === 'recurring' ? 'Recurring Subscription' : 'One-time Payment'}
              </Typography>
            </Box>
            <Box>
              <Typography variant='body2' style={{ fontWeight: 600, marginBottom: '4px' }}>
                Stripe Product ID
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                {task.stripeConfig.stripeProductId}
              </Typography>
            </Box>
            <Box>
              <Typography variant='body2' style={{ fontWeight: 600, marginBottom: '4px' }}>
                Auto-complete on Payment
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                {task.stripeConfig.autoCompleteOnPayment ? 'Yes' : 'No'}
              </Typography>
            </Box>
            {task.description && (
              <Box>
                <Typography variant='body2' style={{ fontWeight: 600, marginBottom: '4px' }}>
                  Instructions for Volunteers
                </Typography>
                <Typography variant='body2' color='textSecondary' style={{ whiteSpace: 'pre-wrap' }}>
                  {task.description}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {loading ? (
        <TaskDetailSkeleton />
      ) : (
        <Box className={classes.field}>
          <Box className='header' style={{ marginBottom: '20px' }}>
            <Typography className='title'>Fields</Typography>
          </Box>
          <Box display='flex' flexDirection='column' style={{ gap: '28px' }}>
            {(showAll ? rootTaskIds : rootTaskIds.slice(0, 3)).map((taskItemId) => (
              <TaskItemField key={taskItemId} items={parseTaskItems} taskItemId={taskItemId} />
            ))}
            {rootTaskIds.length > 3 && (
              <Box alignSelf='stretch' display='flex' alignItems='center' flexDirection='colmn' justifyContent='center'>
                <Button
                  endIcon={showAll ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                  onClick={() => setShowAll(!showAll)}
                  className={classes.showMoreButton}
                  variant='outlined'
                  disableElevation
                >
                  {showAll ? 'Show Less' : 'Show More'}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {loading ? (
        <TaskDetailSkeleton />
      ) : (
        <Box className={classes.field}>
          <Box className='header'>
            <Typography className='title'>Visibility</Typography>
          </Box>
          <Typography className={'subTitle2'}>
            This task is visible to <strong>{task?.applicantsOnly ? 'new applicants' : 'volunteers'}</strong> in{' '}
            <strong>{programText}</strong>, enrolled in <strong>{activityText}</strong>, for <strong>{roleText}</strong>
            . To see a task, a volunteer must satisfy each condition.{' '}
            {task?.adminOnly ? (
              <span>
                Task submissions are <strong>only</strong> visible to <strong>Admins</strong>.
              </span>
            ) : (
              ''
            )}
          </Typography>
          <Box className={classes.visibilityField}>
            <Typography className='title'>Programs</Typography>
            <Box className='items' style={{ flexWrap: 'wrap' }}>
              {task?.programs.length === 0 ? (
                <Typography style={{ fontSize: '14px', color: theme.color.grey.neutralBrand500 }}>
                  This task is visible to all programs
                </Typography>
              ) : null}
              {task?.programs.map((program) => (
                <Box className='item' style={{ margin: '3px' }}>
                  {program.name}
                </Box>
              ))}
            </Box>
          </Box>
          <Box className={classes.visibilityField}>
            <Typography className='title'>Activities</Typography>
            <Box className='items' style={{ flexWrap: 'wrap' }}>
              {task?.requiresAnyActivity ? (
                <Typography style={{ fontSize: '14px', color: theme.color.grey.neutralBrand500 }}>
                  This task is visible to anyone enrolled in at least one activity, including activities added later
                </Typography>
              ) : task?.activities.length === 0 ? (
                <Typography style={{ fontSize: '14px', color: theme.color.grey.neutralBrand500 }}>
                  This task is visible to all activities
                </Typography>
              ) : (
                task?.activities.map((activity) => (
                  <Box className='item' style={{ margin: '3px' }}>
                    {activity.name}
                  </Box>
                ))
              )}
            </Box>
          </Box>
          <Box className={classes.visibilityField}>
            <Typography className='title'>Roles</Typography>
            <Box className='items' style={{ flexWrap: 'wrap' }}>
              {task?.roles.map((role) => (
                <Box className='item' style={{ margin: '3px' }}>
                  {role.name}
                </Box>
              ))}
              <Box className='items'>
                {task?.roles.length === 0 ? (
                  <Typography style={{ fontSize: '14px', color: theme.color.grey.neutralBrand500 }}>
                    This task is visible to all roles
                  </Typography>
                ) : null}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      <TaskVisibilityPreviewModal open={openPreviewDialog} onClose={() => setOpenPreviewDialog(false)} task={task} />
      {openMoveToDialog && task && (
        <MoveToDialog
          task={task}
          cakeOptions={cakeOptions}
          onMove={onMove}
          isOpen={openMoveToDialog}
          handleClose={() => {
            setOpenMoveToDialog(false);
          }}
        />
      )}
    </Box>
  );
}
